import { ScreenEventName } from "@shared/segmentEvents";
import { QueryClient, useQueryClient } from "@tanstack/vue-query";
import {
  type Category,
  type CategoryPreferences,
  CategoryType,
  type Event,
  type ProductDetails,
} from "mws-ts-v2-sdk";

import { queries } from "~/queries";
import { meCategoryPreferenceDefaultParams } from "~/queries/catalog/mePreferenceCategories";
import { getUniversalAxios } from "~/utils/axios/getUniversalAxios";

import type { LocationQuery, RouteParams } from "#vue-router";

type PageCategoryDetails = {
  sport_category: string | undefined;
  promotion_category: string | undefined;
  team_name: string | undefined;
  league_name: string | undefined;
  country_name: string | undefined;
  athlete_name: string | undefined;
  other_category: string | undefined;
};

export const getDetailsFromCategories = (
  pageCategories: Category[],
): PageCategoryDetails => ({
  sport_category: pageCategories?.find(
    (c) => c.externalReference?.type === CategoryType.Sport,
  )?.name,
  promotion_category: pageCategories?.find(
    (c) => c.externalReference?.type === CategoryType.Other,
  )?.name,
  team_name: pageCategories?.find(
    (c) => c.externalReference?.type === CategoryType.Team,
  )?.name,
  league_name: pageCategories?.find(
    (c) => c.externalReference?.type === CategoryType.League,
  )?.name,
  country_name: pageCategories?.find(
    (c) => c.externalReference?.type === CategoryType.Country,
  )?.name,
  athlete_name: pageCategories?.find(
    (c) => c.externalReference?.type === CategoryType.Athlete,
  )?.name,
  other_category: pageCategories?.find(
    (c) => c.externalReference?.type === CategoryType.Other,
  )?.name,
});

const routeNameToScreenMap: Record<string, ScreenEventName> = {
  index: ScreenEventName.Home,
  "my-account": ScreenEventName.Account,
  "my-account-my-profile": ScreenEventName.Profile,
  "my-account-my-bids": ScreenEventName.MyBids,
  "my-account-my-orders": ScreenEventName.MyOrders,
  "product-productIdentifier": ScreenEventName.Product,
  "category-categorySlug": ScreenEventName.CategoryPage,
  "event-eventSlug": ScreenEventName.EventPage,
};

const getCategoriesDetailsRelatedToPage = (
  routeParams: RouteParams,
  platformId: string | undefined,
  queryClient: QueryClient,
): PageCategoryDetails | undefined => {
  const productIdentifier = routeParams.productIdentifier;
  if (
    productIdentifier &&
    typeof productIdentifier === "string" &&
    platformId
  ) {
    const product = queryClient.getQueryData<ProductDetails>(
      queries.products.details(productIdentifier, platformId).queryKey,
    );

    return getDetailsFromCategories(product?.categories ?? []);
  }

  const eventSlug = routeParams.eventSlug;
  if (eventSlug && typeof eventSlug === "string" && platformId) {
    const event = queryClient.getQueryData<Event>(
      queries.events.details(eventSlug, platformId).queryKey,
    );

    return getDetailsFromCategories(event?.categories ?? []);
  }

  const categorySlug = routeParams.categorySlug;
  if (categorySlug && typeof categorySlug === "string") {
    const category = queryClient.getQueryData<Category>(
      queries.categories.details(categorySlug).queryKey,
    );

    if (!category) return;

    const relatedCategories = queryClient.getQueryData<Category[]>(
      queries.categories.relatedCategories(category.slug).queryKey,
    );

    return {
      ...getDetailsFromCategories(relatedCategories ?? []),
      ...(category?.externalReference?.type === CategoryType.Sport && {
        sport_category: category.name,
      }),
    };
  }
};

const storeTrackingQueryParameters = (
  query: LocationQuery,
  trackerId: string,
  trackerName: string,
) => {
  if (query[trackerId]) {
    getUniversalAxios().defaults.headers.common[trackerName] =
      query[trackerId].toString();
    const cookie = useCookie(trackerId, { maxAge: 60 * 60 * 24 * 90 });
    cookie.value = query[trackerId].toString();
  }
};

export default defineNuxtRouteMiddleware((to, from) => {
  const runtimeConfig = useRuntimeConfig();
  if (runtimeConfig.public.nodeEnv === "test") return; // we don't want the analytics to run for tests

  if (import.meta.server) return; // we only want to send page analytics on the client side

  const platformStore = usePlatformStore();
  const platformId = platformStore.tryGetPlatformConfiguration?.platformId;

  // something went wrong loading the platform
  if (!platformId) return;

  const nuxtApp = useNuxtApp();
  const { $segment, $i18n } = nuxtApp;
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { pageViewed } = useAnalytics();
  const { deviceType } = useScreenSize();

  const trackingParams = [
    { id: "gclid", header: "Google-Click-Id" },
    { id: "gbraid", header: "GBraid" },
    { id: "wbraid", header: "WBraid" },
    { id: "rdt_cid", header: "rdtcid" },
  ];

  trackingParams.forEach(({ id, header }) => {
    storeTrackingQueryParameters(to.query, id, header);
  });

  // Don't track query paramater changes by only tracking if the route path changes.
  // Route path does not include query parameters
  if (!from.name || from.path !== to.path) {
    // TODO: ugly hack, but have not found a proper way to deal with this yet. ~ KR.
    setTimeout(() => {
      if (!$segment) {
        // in case there is an error in the plugins (e.g. loading the platform)
        return;
      }

      const cleanedName = to.name
        ?.toString()
        .substring(0, to.name.toString().indexOf("___"));

      if (!cleanedName) return;

      const eventPageName = routeNameToScreenMap[cleanedName] ?? cleanedName;

      const followedCategories: CategoryPreferences | undefined =
        queryClient.getQueryData(
          queries.followedCategories.list({
            ...meCategoryPreferenceDefaultParams,
            xPlatformKey: platformId,
          }).queryKey,
        );

      const followedTeamNames = followedCategories?.results
        .filter((c) => c.type === CategoryType.Team)
        .map((c) => c.name);

      // Add default so E2E tests don't break
      const toParams = to.params || {};

      const categoriesDetails = getCategoriesDetailsRelatedToPage(
        toParams,
        platformId,
        queryClient,
      );

      $segment.browser.value.page("Page", eventPageName, {
        referrer: document?.referrer,
        platform_id: platformId,
        followed_team_names: followedTeamNames ?? [],
        logged_in: auth?.isLoggedIn.value ?? false,
        page_type: eventPageName,
        channel: "web",
        viewport_size: deviceType.value,
        ...categoriesDetails,
      });

      // Work around to send data with page event to Facebook Pixel
      pageViewed({
        ...(Object.keys(toParams).length > 0 && {
          params: toParams,
        }),
        ...categoriesDetails,
        page_type: eventPageName,
        raw_path: to.path,
        unlocalized_path:
          to.path === `/${$i18n.locale.value}`
            ? "/"
            : to.path.replace(`/${$i18n.locale.value}/`, "/"),
      });
    }, 2000);
  }
});
